import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Star, MapPin, ArrowLeft, Check, CalendarCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { auParams } from "@/lib/auMode";

const fmt = (n, cur) => {
  const val = Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
  return `${cur === "NZD" ? "NZ$" : "$"}${val}`;
};

export default function ClinicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [booking, setBooking] = useState(null);
  const [booked, setBooked] = useState({});
  const [reviews, setReviews] = useState(null);
  const [myRating, setMyRating] = useState(5);
  const [myComment, setMyComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    setLoading(true);
    const cfg = { params: auParams(user?.country) };
    api.get(`/clinics/${slug}`, cfg)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    api.get(`/clinics/${slug}/reviews`, cfg).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug, user]);

  const submitReview = async () => {
    if (!user) { login(); return; }
    if (!myComment.trim()) { toast.error("Please write a short review"); return; }
    setSubmittingReview(true);
    try {
      await api.post(`/clinics/${slug}/reviews`, { rating: myRating, comment: myComment });
      const r = await api.get(`/clinics/${slug}/reviews`, { params: auParams(user?.country) });
      setReviews(r.data);
      setMyComment("");
      toast.success("Thanks for your review!");
    } catch (e) {
      toast.error("Could not submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const login = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestConsult = async (treatment) => {
    if (!user) { login(); return; }
    setBooking(treatment);
    try {
      const res = await api.post("/consults", {
        clinic_name: data.clinic.name,
        procedure: treatment || "General consultation",
        location: data.clinic.location,
      });
      setBooked((b) => ({ ...b, [treatment || "__general"]: true }));
      toast.success(res.data.message);
    } catch (e) {
      toast.error("Could not request consult");
    } finally {
      setBooking(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-cream">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-cream gap-4 px-6 text-center">
        <p className="font-heading text-2xl">Clinic not found</p>
        <Button onClick={() => navigate("/")} className="rounded-full bg-brand-primary text-white">Back home</Button>
      </div>
    );
  }

  const { clinic, menu, currency, compliance_note } = data;

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="clinic-brand-logo">
            <Sparkles className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl">AuraLift</span>
          </Link>
          <Button data-testid="clinic-book-btn" onClick={() => requestConsult("General consultation")} disabled={booking === "General consultation"} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2">
            {booking === "General consultation" ? <Loader2 className="w-4 h-4 animate-spin" /> : booked["General consultation"] ? <Check className="w-4 h-4" /> : <CalendarCheck className="w-4 h-4" />}
            {booked["General consultation"] ? "Consult requested" : "Book a consult"}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-brand-primary text-white px-6 py-14">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-6" data-testid="clinic-back-btn">
            <ArrowLeft className="w-4 h-4" /> All clinics
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl mb-3" data-testid="clinic-name">{clinic.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {clinic.location}</span>
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-brand-accent fill-brand-accent" /> {clinic.rating}</span>
              </div>
              <p className="text-white/70 mt-4 max-w-xl leading-relaxed">{clinic.about}</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {clinic.specialties.map((s) => (
                  <span key={s} className="text-xs px-3 py-1 rounded-full bg-white/10">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment menu */}
      <section className="px-6 py-14">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-heading text-2xl sm:text-3xl">Treatments &amp; pricing</h2>
            <span className="text-xs text-brand-slate">Prices in {currency} · casual rates</span>
          </div>
          <p className="text-brand-slate mb-10 max-w-lg text-sm">Indicative pricing — your final quote is confirmed at a free consultation. Member rates apply to treatment plan members.</p>

          {compliance_note && (
            <div data-testid="au-compliance-note" className="mb-10 rounded-2xl border border-brand-accent/30 bg-brand-accent/5 p-5 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-brand-slate leading-relaxed">{compliance_note}</p>
            </div>
          )}

          <div className="space-y-12" data-testid="treatment-menu">
            {menu.map((cat, ci) => (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: ci * 0.05 }}
              >
                <div className="mb-5">
                  <h3 className="font-heading text-xl text-brand-primary">{cat.category}</h3>
                  <p className="text-sm text-brand-slate">{cat.blurb}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {cat.treatments.map((t) => (
                    <div key={t.name} data-testid={`treatment-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="flex items-start justify-between gap-3 p-4 rounded-2xl bg-white border border-brand-border/60">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-brand-slate">{t.description}</p>
                        <button
                          data-testid={`book-treatment-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => requestConsult(t.name)}
                          disabled={booking === t.name || booked[t.name]}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-brand-primary hover:underline disabled:text-brand-slate disabled:no-underline"
                        >
                          {booking === t.name ? <Loader2 className="w-3 h-3 animate-spin" /> : booked[t.name] ? <Check className="w-3 h-3" /> : <CalendarCheck className="w-3 h-3" />}
                          {booked[t.name] ? "Requested" : "Request consult"}
                        </button>
                        {t.preview_procedure && (
                          <button
                            data-testid={`preview-treatment-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            onClick={() => navigate(`/dashboard?procedure=${t.preview_procedure}`)}
                            className="mt-2 ml-3 inline-flex items-center gap-1 text-xs text-brand-accent hover:underline"
                          >
                            <Sparkles className="w-3 h-3" /> Preview on my photo
                          </button>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-brand-primary">{fmt(t.casual, currency)}</p>
                        {t.member != null && <p className="text-[11px] text-brand-accent">Member {fmt(t.member, currency)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Reviews */}
          {reviews?.hidden_for_au ? (
            <div className="mt-16 rounded-2xl border border-brand-border/60 bg-white p-6" data-testid="reviews-hidden-au">
              <h2 className="font-heading text-xl mb-2">Patient reviews</h2>
              <p className="text-sm text-brand-slate">{reviews.note}</p>
            </div>
          ) : reviews && (
            <div className="mt-16" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl sm:text-3xl">Patient reviews</h2>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-brand-accent fill-brand-accent" />
                  <span className="font-heading text-xl" data-testid="reviews-average">{reviews.average}</span>
                  <span className="text-sm text-brand-slate">({reviews.count})</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {reviews.reviews.map((rv, idx) => (
                  <div key={idx} className="p-5 rounded-2xl bg-white border border-brand-border/60" data-testid={`review-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">{rv.author}</p>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= rv.rating ? "text-brand-accent fill-brand-accent" : "text-brand-border"}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-brand-slate">{rv.comment}</p>
                    {rv.date && <p className="text-[11px] text-brand-slate/70 mt-2">{rv.date}</p>}
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-2xl bg-white border border-brand-border/60" data-testid="review-form">
                <p className="font-heading text-lg mb-3">Leave a review</p>
                {user ? (
                  <>
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} data-testid={`review-star-${n}`} onClick={() => setMyRating(n)} className="p-0.5">
                          <Star className={`w-6 h-6 ${n <= myRating ? "text-brand-accent fill-brand-accent" : "text-brand-border"}`} />
                        </button>
                      ))}
                    </div>
                    <textarea
                      data-testid="review-comment"
                      value={myComment}
                      onChange={(e) => setMyComment(e.target.value)}
                      rows={3}
                      placeholder="Share your experience…"
                      className="w-full rounded-xl border border-brand-border p-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                    />
                    <Button data-testid="review-submit-btn" onClick={submitReview} disabled={submittingReview} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2">
                      {submittingReview && <Loader2 className="w-4 h-4 animate-spin" />} Post review
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm text-brand-slate">Sign in to leave a review.</p>
                    <Button data-testid="review-login-btn" onClick={login} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Sign in</Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-14 rounded-3xl bg-brand-sand/70 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-xl mb-1">Ready to see your results first?</h3>
              <p className="text-sm text-brand-slate">Visualise your treatment with AuraLift, then book a consult with {clinic.name}.</p>
            </div>
            <Button data-testid="clinic-cta-btn" onClick={login} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 h-11 px-6 shrink-0">
              <Sparkles className="w-4 h-4" /> Try AuraLift free
            </Button>
          </div>
          <p className="text-xs text-brand-slate mt-6 flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> All treatments are performed by qualified practitioners. Prices and availability are indicative and confirmed by the clinic at consultation.
          </p>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
